import React, { createContext, useContext, ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ProfileDTO } from '../../../shared/types';
import axiosService from '../services/axios.service';
import { useAuth } from './useAuth';

interface ImpersonationStatusResponse {
    isImpersonating: boolean;
    profile: ProfileDTO | null;
    startedAt?: Date;
}

interface ImpersonationContextType {
    isImpersonating: boolean;
    impersonatedProfile: ProfileDTO | null;
    startedAt: Date | null;
    loading: boolean;
    startImpersonation: (crewSlug: string, profileId: string) => Promise<void>;
    stopImpersonation: () => Promise<void>;
    refreshStatus: () => Promise<void>;
}

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined);

interface ImpersonationProviderProps {
    children: ReactNode;
}

// Fetch impersonation status
const fetchImpersonationStatus = async (): Promise<ImpersonationStatusResponse> => {
    const response = await axiosService.get('/admin/impersonation-status');
    return response.data.data;
};

// Start impersonation
const startImpersonationRequest = async (data: { crewSlug: string; profileId: string }) => {
    const response = await axiosService.post('/admin/impersonate', data);
    return response.data;
};

// Stop impersonation
const stopImpersonationRequest = async () => {
    const response = await axiosService.post('/admin/stop-impersonation');
    return response.data;
};

export const ImpersonationProvider = ({ children }: ImpersonationProviderProps) => {
    const queryClient = useQueryClient();
    const { user } = useAuth();

    // Only fetch impersonation status if user is a Persera admin
    const isPerseraAdmin = user?.isAdmin === true;

    // Query for impersonation status
    const { data: statusData, isLoading } = useQuery({
        queryKey: ['impersonationStatus'],
        queryFn: fetchImpersonationStatus,
        staleTime: 5 * 60 * 1000, // 5 minutes
        retry: false,
        refetchOnWindowFocus: false,
        enabled: isPerseraAdmin // Only run query for Persera admins
    });

    // Mutation to start impersonation
    const startMutation = useMutation({
        mutationFn: startImpersonationRequest,
        onSuccess: () => {
            // Invalidate crew member cache to fetch the impersonated profile
            queryClient.invalidateQueries({ queryKey: ['crewMember'] });
            // Reload the page to update all contexts with new profile
            window.location.reload();
        },
        onError: (error) => {
            console.error('Failed to start impersonation:', error);
        }
    });

    // Mutation to stop impersonation
    const stopMutation = useMutation({
        mutationFn: stopImpersonationRequest,
        onSuccess: () => {
            // Invalidate crew member cache to fetch the admin's actual profile
            queryClient.invalidateQueries({ queryKey: ['crewMember'] });
            // Reload the page to restore admin's normal view
            window.location.reload();
        },
        onError: (error) => {
            console.error('Failed to stop impersonation:', error);
        }
    });

    const startImpersonation = async (crewSlug: string, profileId: string): Promise<void> => {
        await startMutation.mutateAsync({ crewSlug, profileId });
    };

    const stopImpersonation = async (): Promise<void> => {
        await stopMutation.mutateAsync();
    };

    const refreshStatus = async (): Promise<void> => {
        await queryClient.invalidateQueries({ queryKey: ['impersonationStatus'] });
    };

    const value: ImpersonationContextType = {
        isImpersonating: statusData?.isImpersonating ?? false,
        impersonatedProfile: statusData?.profile ?? null,
        startedAt: statusData?.startedAt ? new Date(statusData.startedAt) : null,
        loading: isLoading || startMutation.isPending || stopMutation.isPending,
        startImpersonation,
        stopImpersonation,
        refreshStatus
    };

    return (
        <ImpersonationContext.Provider value={value}>
            {children}
        </ImpersonationContext.Provider>
    );
};

export const useImpersonation = (): ImpersonationContextType => {
    const context = useContext(ImpersonationContext);
    if (!context) {
        throw new Error('useImpersonation must be used within an ImpersonationProvider');
    }
    return context;
};
